/**
 * Copy for the persistent top-right story card while the AI assistant
 * (Episode 6, ChatGPT-in-the-browser) is on screen. AiAssistant.tsx reports
 * one of these stage keys through `onStageChange`; App.tsx looks the key up.
 *
 * Rules for every entry (the learner reads this next to the screen, so it must
 * never describe a control that is not visible at that stage):
 *   - name the control and say where it sits;
 *   - one next action per stage;
 *   - `actionLabel` is a phrase inside `body` that App.tsx renders as a
 *     clickable inline action (it clicks the matching button on screen).
 */
export interface AiStageGuide {
  title: string;
  body: string;
  actionLabel?: string;
}

export const AI_ASSISTANT_STAGE_GUIDE: Record<string, AiStageGuide> = {
  home: {
    title: "Open ChatGPT",
    body: "Priya asked you to summarise a contract with ChatGPT. Click the blinking ChatGPT bookmark in the bookmarks bar to open it.",
  },
  intro: {
    title: "Follow the short tutorial",
    body: "Account, then files, then check and share. Read each card on the left of the chat, then carry on.",
  },
  account: {
    title: "Choose your account",
    body: "You work for NorthBay, so pick NorthBay Corporate AI. A personal AI account is not managed by the company and is a serious risk. Click your account at the top right of the chat.",
  },
  account_menu: {
    title: "Pick your account",
    body: "You work for NorthBay, so pick NorthBay Corporate AI in the menu. A personal account is not managed by the company and is a serious risk. The yellow note on the menu says why.",
  },
  attach: {
    title: "Review your files",
    body: "You have been asked to decide, for each file, whether it is safe to upload. Click the paperclip at the bottom left of the chat box.",
  },
  docs_deciding: {
    title: "Decide this file",
    body: "Choose what happens to the file on screen: send it as-is, redact it first, or do not attach it. You must decide every file, one at a time.",
  },
  docs_redacting: {
    title: "Black out the sensitive lines",
    body: "Click every line that holds personal, financial or secret details. When you are done, click Attach redacted copy.",
    actionLabel: "Attach redacted copy",
  },
  docs_ready: {
    title: "Check your choices",
    body: "Every file now has a decision. Next step: Check your choices, to look over what will be sent before anything is sent.",
    actionLabel: "Check your choices",
  },
  review: {
    title: "Check before you send",
    body: "Look over the account, the message and each file as it will go out. When you are happy, click Send to ChatGPT.",
    actionLabel: "Send to ChatGPT",
  },
  sending: {
    title: "Your request is going out",
    body: "ChatGPT is reading your files and drafting the summary. Wait for the draft. You do not need to click anything.",
  },
  reply: {
    title: "Get it to Priya",
    body: "The summary now exists. This last step is about the safest way to get it to Priya. Click Share to decide.",
    actionLabel: "Share",
  },
  share: {
    title: "Choose how to share",
    body: "Pick how the summary reaches Priya. Think about who else could end up able to read it.",
  },
  result: {
    title: "See how you did",
    body: "Read each page of the report: your choices next to the safe choices, then what left the company.",
  },
  linkescape: {
    title: "Where a public link can travel",
    body: "See how one public link can spread far beyond the person you sent it to.",
  },
};
